import type { Key } from "react";
import { Button } from "@/shared/components/button/button-untitledui";
import {
	DateRangeType,
	getDefaultSettings,
	QuestionOptionDisplayType,
	QuestionType,
} from "@/types/question";
import {
	createEmptySelectionOptions,
	defaultChecklistOptions,
} from "../../constants/questionDefaults";
import {
	type QuestionTypeOrChecklist,
	useQuestionForm,
} from "../../hooks/useQuestionForm";
import { useQuestionSubmit } from "../../hooks/useQuestionSubmit";
import { getRenderedOptions } from "../../utils";
import { FeedbackMessages } from "../FeedbackMessages";
import { QuestionPreview } from "../QuestionPreview";
import { QuestionSettingsBlock } from "../QuestionsSettings";
import { QuestionPreviewBox } from "../question-preview-box";
import { OptionsBlock } from "./OptionsBlock";
import { QuestionForm } from "./QuestionForm";

type QuestionEditorProps = {
	onQuestionSaved?: () => void;
};

export const QuestionEditor = (_props: QuestionEditorProps) => {
	const {
		title,
		setTitle,
		description,
		setDescription,
		selectedType,
		setSelectedType,
		settings,
		setSettings,
		options,
		setOptions,
		handleAddOption,
		handleUpdateOption,
		handleDeleteOption,
		handleReorderOptions,
	} = useQuestionForm();

	const {
		isLoading,
		error,
		setError,
		fieldErrors,
		setFieldErrors,
		success,
		handleSave,
	} = useQuestionSubmit();

	const handleTypeChange = (key: Key | null) => {
		if (!key) {
			return;
		}

		const newType = Number(key) as QuestionTypeOrChecklist;
		setSelectedType(newType);

		if (newType === QuestionType.Boolean) {
			setDescription("");
		}

		if (newType === 999) {
			setOptions(defaultChecklistOptions.map((opt) => ({ ...opt })));
			setSettings({
				...getDefaultSettings(QuestionType.Option),
				optionDisplayType: QuestionOptionDisplayType.Icon,
			});
		} else if (newType === QuestionType.Option) {
			setOptions(createEmptySelectionOptions());
			setSettings({
				...getDefaultSettings(newType),
				optionDisplayType: QuestionOptionDisplayType.Checkbox,
			});
		} else if (newType === QuestionType.Date) {
			setSettings({
				...getDefaultSettings(newType as QuestionType),
				dateRangeType: DateRangeType.Any,
				minDate: undefined,
				maxDate: undefined,
			});
		} else {
			setSettings(getDefaultSettings(newType as QuestionType));
		}
	};

	const showOptionsBlock =
		selectedType === QuestionType.Option || selectedType === 999;
	const renderedOptions = getRenderedOptions(selectedType, options);
	const hasIncompleteOptions =
		selectedType === QuestionType.Option &&
		options.some((option) => !option.text.trim());

	return (
		<div className="rounded-2xl border border-gray-300 bg-white p-6 shadow-sm">
			<div className="space-y-6">
				<FeedbackMessages
					error={error}
					fieldErrors={fieldErrors}
					success={success}
				/>

				<QuestionForm
					description={description}
					fieldErrors={fieldErrors}
					onTypeChange={handleTypeChange}
					selectedType={selectedType}
					setDescription={setDescription}
					setError={setError}
					setFieldErrors={setFieldErrors}
					setTitle={setTitle}
					title={title}
				/>

				<QuestionSettingsBlock
					selectedType={selectedType}
					setError={setError}
					setSettings={setSettings}
					settings={settings}
				/>

				{showOptionsBlock && selectedType !== null ? (
					<OptionsBlock
						onAddOption={handleAddOption}
						onDeleteOption={handleDeleteOption}
						onReorderOptions={handleReorderOptions}
						onUpdateOption={handleUpdateOption}
						options={renderedOptions}
						selectedType={selectedType}
						setSettings={setSettings}
						settings={settings}
					/>
				) : null}

				{selectedType !== null ? (
					<QuestionPreviewBox>
						<QuestionPreview
							displayType={settings.optionDisplayType}
							options={renderedOptions.map((option, index) => ({
								...option,
								text: option.text.trim() || `Opção ${index + 1}`,
							}))}
							settings={settings}
							title={title}
							type={selectedType}
						/>
					</QuestionPreviewBox>
				) : null}
			</div>

			<div className="flex items-center justify-end pt-4">
				<Button
					color="primary"
					isDisabled={hasIncompleteOptions || !title.trim()}
					isLoading={isLoading}
					onClick={() =>
						handleSave(title, selectedType, settings, options, description)
					}
					showTextWhileLoading
					size="sm"
				>
					{isLoading ? "Salvando..." : "Salvar pergunta"}
				</Button>
			</div>
		</div>
	);
};
